import React, { PropsWithChildren, useState } from 'react';
import * as navigate from 'app/area/shared/navigation';
import { MdAdd } from 'react-icons/md';
import { AddButton } from 'ui';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { LoadOptions } from 'app/types-shared';
import { DragListContainer } from 'app/area/shared/list-components/drag-list-container';
import { PopupAddNew } from 'app/area/shared/list-components/popup-add-new';
import { ExternalUrlDragItemType } from 'app/area/external-url-groups/form/constants';
import { ExternalUrlsProps, ExternalUrlValue } from 'app/area/external-url-groups/form/types-store';
import { getNextElement, getPrevElement } from 'app/area/external-url-groups/form/utils/get-external-url';
import { ExternalUrlTile } from 'app/area/external-url-groups/form/components/external-url-tile';
import { ExternalUrlDragPreviewItem } from 'app/area/external-url-groups/form/components/external-url-drag-preview-item';
import { renderExternalUrlDragPreview } from 'app/area/external-url-groups/form/components/external-url-drag-preview';
import { createExternalUrlSelectedValues } from 'app/area/external-url-groups/form/utils/create-external-url';
import { createExternalUrls } from 'app/area/external-url-groups/form/utils/create-external-urls';
import { AddExternalUrlType } from 'app/area/external-url-groups/form/types';

const getKey = (id: number, sequence: number) => isDefinedIncludingZero(id)
  ? `id_${id}` : `sequence_${sequence}`;

export const ExternalUrl = ({
  externalUrls, fetchingAddExternalUrls, externalUrlOptions, externalUrlsValidation,
  translation, locale, loadExternalUrlOptions, deleteAllExternalUrls, addExternalUrls,
  updateExternalUrl, deleteExternalUrl, updateValidation, displayError

}: PropsWithChildren<ExternalUrlsProps>) => {

  const onLoadExternalUrlOptions = (loadOptionsParams: LoadOptions) => {
    loadExternalUrlOptions(loadOptionsParams);
  };

  const [isAddNewDefaultOpen, setIsAddNewDefaultOpen] = useState(false);
  const [isAddNewItemOpen, setIsAddNewItemOpen] = useState(false);
  const [addLinkSequence, setAddLinksSequence] = useState<number>();

  const closeAddExternalUrlDefault = () => {
    setIsAddNewDefaultOpen(false);
  };

  const openAddExternalUrlDefault = () => {
    setIsAddNewDefaultOpen(true);
  };

  const onAddExternalUrlDefault = (values: ExternalUrlValue[]) => {
    const urls = createExternalUrls(values);
    addExternalUrls(AddExternalUrlType.DEFAULT, urls, undefined);
  };

  const closeAddExternalUrlItem = () => {
    setIsAddNewItemOpen(false);
  };

  const openAddExternalUrlItem = (sequence?: number) => {
    setAddLinksSequence(sequence);
    setIsAddNewItemOpen(true);
  };

  const onAddExternalUrlItem = (values: ExternalUrlValue[]) => {
    const existedLinkIds = externalUrls.map(url => url?.code?.value);
    const newValues = values.filter(item => !existedLinkIds.includes(item.value));
    const urls = createExternalUrls(newValues);
    addExternalUrls(AddExternalUrlType.ITEM_SEQUENCE, urls, addLinkSequence ?? externalUrls?.length + 1);
  };

  const onManageExternalUrls = () => {
    navigate.toAddExternalUrl();
  };

  const footer = <AddButton
    icon={MdAdd}
    onClick={openAddExternalUrlDefault}
    disabled={fetchingAddExternalUrls}>
    {translation.external_url_add_url}
  </AddButton>;

  const panelProps = {
    title: translation.external_urls_title,
    selectedValues: createExternalUrlSelectedValues(externalUrls),
    addItemsVisible: true,
    actionTitle: translation.external_url_add_new_url,
    options: externalUrlOptions.values,
    fetchingOptions: externalUrlOptions.fetching || false,
    descriptionAllLevels: false,
    doneLabel: translation.done,
    searchPlaceholder: translation.search,
    locale,
    onAction: onManageExternalUrls,
    loadOptions: onLoadExternalUrlOptions
  };

  return <>
    <ExternalUrlDragPreviewItem
      renderItem={renderExternalUrlDragPreview(translation)}
      itemType={ExternalUrlDragItemType.externalUrlTile} />
    <DragListContainer footer={footer}>
      {externalUrls
        ?.map(
          (externalUrl, index) => {
            const prevElement = getPrevElement(externalUrls, index);
            const nextElement = getNextElement(externalUrls, index);

            return <ExternalUrlTile
              key={getKey(externalUrl.code?.value, externalUrl.sequence)}
              externalUrlItem={externalUrl}
              deleteAllExternalUrls={deleteAllExternalUrls}
              prevElementSequence={prevElement?.sequence}
              nextElementSequence={nextElement?.sequence}
              onAddExternalUrl={openAddExternalUrlItem}
              updateExternalUrl={updateExternalUrl}
              deleteExternalUrl={deleteExternalUrl}
              updateValidation={updateValidation}
              displayError={displayError}
              externalUrlsValidation={externalUrlsValidation}
              translation={translation} />;
          }
        )}
    </DragListContainer>

    {isAddNewDefaultOpen
      && <PopupAddNew
        {...panelProps}
        addItems={onAddExternalUrlDefault}
        onClose={closeAddExternalUrlDefault} />}

    {isAddNewItemOpen
      && <PopupAddNew
        {...panelProps}
        addItems={onAddExternalUrlItem}
        readOnlyItemIds={externalUrls?.map(url => url.code.value)}
        onClose={closeAddExternalUrlItem} />}
  </>;
};
